import {
  getAccessToken,
  isTokenExpired,
  subscribeToken,
} from '../../../core/lib/api';
import { pullAll, flushOutbox } from './index';
import { useVault } from '../../../core/lib/vault';
import {
  handleRemoteUpdate,
  dispatchYjsNak,
  handleYjsSyncRequest,
  handleYjsSyncResponse,
  handleAwarenessRemoteUpdate,
  getLoadedDocIds,
  requestYjsSync,
} from '../lib/yjsCollab';

let ws: WebSocket | null = null;
let backoff = 1000;
let stopped = false;
let heartbeat: ReturnType<typeof setInterval> | null = null;
let lastPong = 0;

type ConnectivityListener = (online: boolean) => void;
const connectivityListeners = new Set<ConnectivityListener>();
type ChangeListener = (ev: { resource: string; id: string; action: string }) => void;
const changeListeners = new Set<ChangeListener>();
type HeartbeatListener = () => void;
const heartbeatListeners = new Set<HeartbeatListener>();

/**
 * Subscribe to a tick fired on every successful WS pong (~every 15 s while
 * the socket is healthy). Lets the StatusLight render a tiny pulse so the
 * user sees real-time confirmation that we're still bidirectional with the
 * server, distinct from the static "connected" state.
 */
export function subscribeHeartbeat(cb: HeartbeatListener): () => void {
  heartbeatListeners.add(cb);
  return () => heartbeatListeners.delete(cb);
}
function notifyHeartbeat() {
  for (const cb of heartbeatListeners) {
    try { cb(); } catch (err) { console.warn('[sync] heartbeat listener threw', err); }
  }
}

export function connectSync() {
  stopped = false;
  openSocket();
}

export function disconnectSync() {
  stopped = true;
  if (heartbeat) {
    clearInterval(heartbeat);
    heartbeat = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
  notifyConnectivity(false);
}

/**
 * "Can we durably sync to the server right now?" — distinct from the bare
 * WebSocket readyState. Returns true only when:
 *   - WebSocket is OPEN, AND
 *   - vault is unlocked (vaultKey present, otherwise the snapshot loop bails
 *     mid-flight — see persistSnapshot in lib/yjsCollab.ts), AND
 *   - JWT is not expired (a continuously-typing user keeps auto-lock at bay
 *     but the JWT can still tick past its TTL; the next WS reconnect will
 *     4401, and pre-reconnect we want StatusLight to read honestly).
 *
 * StatusLight + Workspace use this to surface "Locked — N edits queued" /
 * "Re-auth needed — N edits queued" instead of the misleading green badge
 * we used to show in those states.
 */
export function isConnected(): boolean {
  if (ws == null || ws.readyState !== WebSocket.OPEN) return false;
  if (useVault.getState().vaultKey == null) return false;
  if (isTokenExpired(5)) return false;
  return true;
}

/**
 * Re-emit the current connectivity state. Wires lock/unlock and JWT
 * rotation events into the same listener fan-out as socket open/close so
 * StatusLight subscribers don't need separate plumbing for each.
 */
export function refreshConnectivity(): void {
  notifyConnectivity(isConnected());
}

// Vault-lock and JWT-rotation transitions are real connectivity changes
// from the StatusLight's point of view — re-emit so subscribers re-render.
useVault.subscribe((s, prev) => {
  if ((s.vaultKey == null) !== (prev.vaultKey == null)) refreshConnectivity();
});
subscribeToken(() => refreshConnectivity());

export function subscribeConnectivity(cb: ConnectivityListener): () => void {
  connectivityListeners.add(cb);
  return () => connectivityListeners.delete(cb);
}

/** Subscribe to raw change events (non-debounced — useful for delete auto-nav). */
export function subscribeChanges(cb: ChangeListener): () => void {
  changeListeners.add(cb);
  return () => changeListeners.delete(cb);
}

export function sendWs(msg: unknown): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;
  try {
    ws.send(JSON.stringify(msg));
    return true;
  } catch {
    return false;
  }
}

function notifyConnectivity(online: boolean) {
  for (const cb of connectivityListeners) cb(online);
}

function openSocket() {
  if (stopped) return;
  const token = getAccessToken();
  if (!token) return;
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${location.host}/ws/sync?token=${encodeURIComponent(token)}`;
  ws = new WebSocket(url);

  ws.onopen = () => {
    backoff = 1000;
    lastPong = Date.now();
    refreshConnectivity();
    // Catch up on every doc we still have loaded — peers may have applied
    // updates while our socket was down. Server has no replay buffer, so
    // reconnect-time sync_request is the only path that recovers from a
    // transient disconnect mid-edit. Bounded by editor pane mounts.
    for (const docId of getLoadedDocIds()) {
      requestYjsSync(docId, sendWs);
    }
    // Drain anything queued during the disconnect. Fire-and-forget — the
    // 30 s recurring flush in core/App.tsx and the boot recovery cover
    // anything that doesn't drain on this single attempt.
    void flushOutbox().catch((err) => console.warn('[sync] reconnect flushOutbox', err));
    // Catch up on any peer-side changes that landed while we were offline.
    // Server has no replay buffer for change_event broadcasts, so without
    // this a page another device created during the disconnect stays
    // invisible until the user clicks something that re-fetches. Debounced
    // (250 ms) so a rapid reconnect-loop doesn't hammer the API.
    schedulePull();
    // Start heartbeat: ping every 15s, consider the socket dead if we haven't
    // heard a pong in 45s. The server already replies with { type: 'pong' }
    // to any { type: 'ping' } message (ws/index.ts:40-42).
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastPong > 45_000) {
        // Server missed 3 pings — force a reconnect.
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        return;
      }
      try {
        ws.send(JSON.stringify({ type: 'ping' }));
      } catch {
        /* ignore */
      }
    }, 15_000);
  };
  ws.onmessage = (ev) => {
    let msg: any;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (msg.type === 'pong') {
      lastPong = Date.now();
      notifyHeartbeat();
      return;
    }
    if (msg.type === 'change_event') {
      schedulePull();
      for (const cb of changeListeners) cb(msg);
      return;
    }
    if (msg.type === 'yjs_update' && typeof msg.documentId === 'string' && typeof msg.update === 'string') {
      const vaultKey = useVault.getState().vaultKey;
      if (!vaultKey) return;
      void handleRemoteUpdate(msg.documentId, msg.update, vaultKey);
      return;
    }
    if (
      msg.type === 'yjs_sync_request' &&
      typeof msg.documentId === 'string' &&
      typeof msg.stateVector === 'string' &&
      typeof msg.requesterUserId === 'string'
    ) {
      const vaultKey = useVault.getState().vaultKey;
      if (!vaultKey) return;
      void handleYjsSyncRequest(
        msg.documentId,
        msg.stateVector,
        msg.requesterUserId,
        vaultKey,
        sendWs,
      );
      return;
    }
    if (
      msg.type === 'yjs_sync_response' &&
      typeof msg.documentId === 'string' &&
      typeof msg.update === 'string'
    ) {
      const vaultKey = useVault.getState().vaultKey;
      if (!vaultKey) return;
      void handleYjsSyncResponse(msg.documentId, msg.update, vaultKey);
      return;
    }
    if (
      msg.type === 'awareness_update' &&
      typeof msg.documentId === 'string' &&
      typeof msg.update === 'string'
    ) {
      const vaultKey = useVault.getState().vaultKey;
      if (!vaultKey) return;
      void handleAwarenessRemoteUpdate(msg.documentId, msg.update, vaultKey);
      return;
    }
    if (msg.type === 'yjs_nak' && typeof msg.documentId === 'string' && typeof msg.reason === 'string') {
      dispatchYjsNak({
        documentId: msg.documentId,
        reason: msg.reason,
        maxBytes: typeof msg.maxBytes === 'number' ? msg.maxBytes : undefined,
      });
      return;
    }
  };
  ws.onclose = () => {
    ws = null;
    if (heartbeat) {
      clearInterval(heartbeat);
      heartbeat = null;
    }
    refreshConnectivity();
    if (stopped) return;
    setTimeout(openSocket, backoff);
    backoff = Math.min(backoff * 2, 30_000);
  };
  ws.onerror = () => {
    ws?.close();
  };
}

let pullTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePull() {
  if (pullTimer) return;
  pullTimer = setTimeout(() => {
    pullTimer = null;
    pullAll().catch((err) => console.warn('[sync] pull failed', err));
  }, 250);
}
